import { useEffect, useState, useCallback, useImperativeHandle, forwardRef } from 'react';
import { Loader as Loader2, RotateCcw, CircleCheck as CheckCircle2, Clock, CircleAlert as AlertCircle, Info } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { TERMS_SECTIONS, fetchEffectiveTerms, formatAcceptance, hasAnyTerms, type TermsSections, type EffectiveTerms } from '@/lib/b2bTerms';

interface Props {
  clientId: string;
  companyId: string;
}

export interface B2BClientTermsPanelHandle {
  isDirty: () => boolean;
  save: () => Promise<{ ok: true } | { ok: false; message: string }>;
}

const EMPTY: TermsSections = { sale_terms: '', returns_policy: '', general_notes: '' };

const norm = (v: string | null | undefined) => (v ?? '').trim();
const sameTerms = (a: TermsSections, b: TermsSections) => TERMS_SECTIONS.every(s => norm(a[s.key]) === norm(b[s.key]));

export const B2BClientTermsPanel = forwardRef<B2BClientTermsPanelHandle, Props>(function B2BClientTermsPanel({ clientId, companyId }, ref) {
  const [form, setForm] = useState<TermsSections>(EMPTY);
  const [saved, setSaved] = useState<TermsSections>(EMPTY);
  const [general, setGeneral] = useState<TermsSections>(EMPTY);
  const [generalPublished, setGeneralPublished] = useState(false);
  const [exists, setExists] = useState(false);
  const [status, setStatus] = useState<EffectiveTerms | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const [own, gen] = await Promise.all([
      supabase.from('b2b_client_terms').select('sale_terms, returns_policy, general_notes').eq('client_id', clientId).maybeSingle(),
      supabase.from('b2b_terms').select('sale_terms, returns_policy, general_notes, is_published').eq('company_id', companyId).maybeSingle(),
    ]);
    if (own.error || gen.error) { setError((own.error ?? gen.error)!.message); setLoading(false); return; }
    const values = { sale_terms: own.data?.sale_terms ?? '', returns_policy: own.data?.returns_policy ?? '', general_notes: own.data?.general_notes ?? '' };
    setExists(Boolean(own.data));
    setForm(values);
    setSaved(values);
    setGeneral({ sale_terms: gen.data?.sale_terms ?? '', returns_policy: gen.data?.returns_policy ?? '', general_notes: gen.data?.general_notes ?? '' });
    setGeneralPublished(Boolean(gen.data?.is_published));
    try { setStatus(await fetchEffectiveTerms(clientId)); } catch { setStatus(null); }
    setLoading(false);
  }, [clientId, companyId]);

  useEffect(() => { load(); }, [load]);

  useImperativeHandle(ref, () => ({
    isDirty: () => !loading && !error && !sameTerms(form, saved),
    save: async () => {
      if (loading || error || sameTerms(form, saved)) return { ok: true };
      const payload = {
        client_id: clientId,
        company_id: companyId,
        sale_terms: norm(form.sale_terms) || null,
        returns_policy: norm(form.returns_policy) || null,
        general_notes: norm(form.general_notes) || null,
      };
      const { error: err } = exists
        ? await supabase.from('b2b_client_terms').update(payload).eq('client_id', clientId)
        : await supabase.from('b2b_client_terms').insert(payload);
      if (err) return { ok: false, message: err.message };
      setSaved(form);
      setExists(true);
      return { ok: true };
    },
  }), [loading, error, form, saved, exists, clientId, companyId]);

  if (loading) {
    return <div className="flex items-center gap-2 py-6 text-sm text-gray-400"><Loader2 className="w-4 h-4 animate-spin" /> טוען תנאים…</div>;
  }
  if (error) {
    return <div className="flex items-center gap-2 px-3 py-2.5 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700"><AlertCircle className="w-4 h-4 shrink-0" />{error}</div>;
  }

  const visible = Boolean(status?.published);
  const dirty = !sameTerms(form, saved);

  return (
    <div className="space-y-4">
      {!visible ? (
        <p className="text-xs text-gray-500 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
          ללקוח אין כרגע תנאים להצגה. מלאו כאן תנאים מיוחדים ולחצו "שמור שינויים", או פרסמו תנאים כלליים בהגדרות החברה.
        </p>
      ) : status?.accepted ? (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-100 text-xs text-emerald-800">
          <CheckCircle2 className="w-4 h-4 shrink-0" />
          אישר את הגרסה הנוכחית · {formatAcceptance(status.last_accepted_at, status.last_accepted_by_type, status.last_accepted_by_name)}
        </div>
      ) : (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-100 text-xs text-amber-800">
          <Clock className="w-4 h-4 shrink-0" />
          {status?.last_accepted_at
            ? `גרסה חדשה ממתינה לאישור · אישור אחרון: ${formatAcceptance(status.last_accepted_at, status.last_accepted_by_type, status.last_accepted_by_name)}`
            : 'הלקוח טרם אישר את התנאים'}
        </div>
      )}

      <div className="flex items-start gap-2 text-xs text-gray-500">
        <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
        <span>
          {generalPublished
            ? 'מלאו רק את החלקים שבהם ללקוח זה יש תנאים מיוחדים. חלק ריק יציג ללקוח את התנאים הכלליים של העסק.'
            : 'התנאים הכלליים של העסק לא מפורסמים, לכן הלקוח יראה רק את מה שתמלאו כאן. חלק ריק לא יוצג.'}
        </span>
      </div>

      {TERMS_SECTIONS.map(s => (
        <div key={s.key} className="space-y-1.5">
          <label className="text-sm font-medium text-gray-700">{s.label}</label>
          <textarea
            rows={3} value={form[s.key] ?? ''}
            onChange={e => setForm(f => ({ ...f, [s.key]: e.target.value }))}
            placeholder={generalPublished && general[s.key] ? `כללי: ${general[s.key]}` : 'לא יוצג ללקוח אם יישאר ריק'}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm leading-relaxed placeholder:text-gray-300
                       focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-transparent"
          />
        </div>
      ))}

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <button
          type="button" disabled={!hasAnyTerms(form)}
          onClick={() => setForm(EMPTY)}
          className="flex items-center gap-1.5 px-3 py-2 text-xs text-gray-600 border border-gray-200 rounded-lg
                     hover:bg-gray-50 transition-colors disabled:opacity-40"
        >
          <RotateCcw className="w-3.5 h-3.5" /> ניקוי התנאים המיוחדים
        </button>
        {dirty && (
          <span className="text-xs font-medium text-amber-700">יש שינויים שלא נשמרו · לחצו "שמור שינויים"</span>
        )}
      </div>
    </div>
  );
});
